/**
 * The two dropdown sources the plan pages need, pointed at the Hub.
 *
 * The kit's version carried Furi's whole option catalogue — clients, contracts, activities,
 * a timer poll — none of which these three screens touch. Only the owner and project
 * pickers are used, so only those are here; the rest went with the parts that used them.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { Option } from '../components/ui';

export function useOptions(path: string, key: unknown[], enabled = true) {
  return useQuery({
    queryKey: key,
    queryFn: () => api.get<Option[]>(path),
    // Five minutes: a colleague list does not change while somebody drags a bar.
    staleTime: 300_000,
    enabled,
  });
}

/**
 * Who a task can be given to, and which project the roadmap is showing.
 *
 * Under `/plan/...` rather than the kit's `/users/options` and `/projects/options`. The
 * second of those cannot exist on the Hub: `projects/:id` carries a UUID pipe and matched
 * first, so the picker got a 400 saying "uuid is expected". FRONTEND.md invites this change
 * — these two are the endpoints it tells you to repoint.
 */
/**
 * ALWAYS PASS THE PROJECT. Without it the API can only answer with a flat roster of our own
 * people across every company you can see; with it, it answers with the board's own assignable
 * list for that project — the customer's people included, each carrying whose side they are
 * on. The picker and the save then agree, which they did not before: the plan offered names
 * the API would refuse, and refused none it offered.
 */
export const useUserOptions = (projectId?: string) =>
  useOptions(
    projectId ? `/plan/user-options?projectId=${encodeURIComponent(projectId)}` : '/plan/user-options',
    ['options', 'users', projectId ?? 'all'],
  );

export const useProjectOptions = () => useOptions('/plan/project-options', ['options', 'projects']);
